'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiRequestError } from '@/lib/api';
import { LAUNCH_REQUEST_TIMEOUT_MS } from '@/lib/launch-request';

import type {
  PersonDetectedPayload, TriggerResolvedPayload, TriggerProposalPayload,
} from '@/hooks/useWebSocket';
import type { TriggerConsidered } from '@/components/dashboard/droneMapTypes';

/**
 * The COP's list of unanswered external triggers.
 *
 * SAME TWO-WRITER MODEL as `useCopCoverage` and `useCopMissionsQueue` beside it:
 * the `person_detected` frame is the mechanism, and the poll is the backstop for
 * a socket that dropped without the browser noticing.
 *
 * The backstop is not optional here. Redis pub/sub has no replay, and
 * `useWebSocket` reconnects with backoff on every close - a laptop sleeping, an
 * nginx idle timeout, an API redeploy. A detection that fires inside that gap is
 * gone from the map for ever: the alarm panel still shows the row from its own
 * poll, so the operator is told a person was seen, and the one action this
 * feature exists to offer is missing with nothing on screen saying why.
 *
 * The poll also BOUNDS the list. The server serves a 12 h / 50-row operational
 * window (`triggerEventRepo.findOpenByOrg`) precisely because a camera that keeps
 * seeing people produces up to one row per minute per dock. A live-frames-only
 * list grows all shift, so a COP left open since morning and one just reloaded
 * would show two different air pictures.
 */
const TRIGGER_REFRESH_MS = 30_000;
const TRIGGER_REQUEST_TIMEOUT_MS = 8_000;

export interface TriggerEvent {
  id: string;
  dock_id: string | null;
  source: string;
  type: string;
  score: number | null;
  /** Null when the source reported no valid fix. Such a trigger has no map point. */
  latitude: number | null;
  longitude: number | null;
  occurred_at: string;

  /**
   * `proposed` means the platform named an aircraft and is waiting for a human (#253).
   *
   * Optional because a server that predates automatic dispatch does not send it, and because the
   * live `person_detected` frame carries a brand-new detection, which is always `new`.
   */
  status?: 'new' | 'proposed';
  proposed_drone_id?: string | null;
  proposed_altitude_m?: number | null;
  proposal_mode?: 'propose' | 'full_auto' | null;
  proposal_expires_at?: string | null;
  proposal_considered?: TriggerConsidered[] | null;
  /** Why the platform's last attempt produced no sortie. Null when it has not tried, or it flew. */
  dispatch_refusal?: string | null;
}

interface UseTriggerEvents {
  triggers: TriggerEvent[];
  /**
   * True once a read has failed and no newer one has succeeded. An empty map with
   * no detections and an empty map because we could not ask are the same picture,
   * and on a tactical display that is the one difference that matters.
   */
  degraded: boolean;
  /** Record that an aircraft was sent, and drop the point. */
  markDispatched: (id: string, missionId: string) => Promise<void>;
  /** Clear the point without sending anything. */
  dismiss: (id: string) => Promise<void>;
  /**
   * Say yes to the aircraft the platform named.
   *
   * Resolves with what launched, so the caller can pin the sortie onto the missions board the way
   * the manual dispatch dialog does. A launch the operator authorised and cannot then see is a
   * launch they will authorise again.
   */
  approve: (id: string) => Promise<{ missionId: string; droneId: string | null }>;
  /**
   * Say no to the named aircraft WITHOUT clearing the detection.
   *
   * Deliberately not a dismiss: the person is still there, and the operator may want to send
   * something of their own choosing. The point stays and the proposal comes off it.
   */
  reject: (id: string) => Promise<void>;
}

export function useTriggerEvents(
  enabled: boolean,
  subscribePersonDetected: (cb: (e: PersonDetectedPayload) => void) => () => void,
  subscribeTriggerResolved: (cb: (e: TriggerResolvedPayload) => void) => () => void,
  subscribeTriggerProposal: (cb: (e: TriggerProposalPayload) => void) => () => void,
): UseTriggerEvents {
  const [triggers, setTriggers] = useState<TriggerEvent[]>([]);
  const [degraded, setDegraded] = useState(false);
  /** Issue order, so a slow response cannot overwrite a newer one. */
  const seqRef = useRef(0);
  /**
   * Mirrors `triggers` for the optimistic rollback to read synchronously. The
   * rollback used to capture the removed row from inside a `setState` updater,
   * which React owns the scheduling of - under concurrent rendering it is not
   * guaranteed to have run before an awaited request rejects.
   */
  const triggersRef = useRef<TriggerEvent[]>([]);
  triggersRef.current = triggers;

  const refresh = useCallback(() => {
    const seq = ++seqRef.current;
    api
      .get<{ data: TriggerEvent[] }>('/api/triggers', {
        signal: AbortSignal.timeout(TRIGGER_REQUEST_TIMEOUT_MS),
      })
      .then((res) => {
        if (seq !== seqRef.current) return;
        // MERGED BY ID, not replaced. A poll in flight while a live frame arrives
        // must not drop the row that frame just added - the server may not have
        // committed it when this read was issued.
        setTriggers((prev) => {
          const server = new Map(res.data.map((t) => [t.id, t]));
          for (const local of prev) {
            if (!server.has(local.id)) continue;
            server.set(local.id, server.get(local.id)!);
          }
          const merged = [...server.values()];
          const known = new Set(merged.map((t) => t.id));
          // Anything the server no longer lists has left the operational window
          // or been answered elsewhere; the ONLY rows kept beyond it are ones
          // that arrived after this request was issued.
          const fresher = prev.filter((t) => !known.has(t.id) && !res.data.some((s) => s.id === t.id)
            && new Date(t.occurred_at).getTime() > Date.now() - TRIGGER_REQUEST_TIMEOUT_MS);
          return [...fresher, ...merged].sort(
            (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
          );
        });
        setDegraded(false);
      })
      .catch(() => {
        if (seq !== seqRef.current) return;
        // The list is NOT cleared. Blanking it on a failed poll would remove every
        // point from the air picture and read as "nothing is happening".
        setDegraded(true);
      });
  }, []);

  // Initial read plus the backstop, exactly as the sibling hooks do it.
  useEffect(() => {
    if (!enabled) return;
    refresh();
    const timer = setInterval(refresh, TRIGGER_REFRESH_MS);
    return () => clearInterval(timer);
  }, [enabled, refresh]);

  // Live detections.
  useEffect(() => {
    if (!enabled) return;
    return subscribePersonDetected((e) => {
      // A detection whose row could not be stored has no id, so it cannot be
      // dispatched against or dismissed. Dropping it here rather than drawing
      // a point that refuses every click - the alarm row still carries it.
      if (!e.trigger_id) return;
      const id = e.trigger_id;
      setTriggers((prev) => (
        // The socket can redeliver across a reconnect.
        prev.some((t) => t.id === id) ? prev : [{
          id,
          dock_id: e.dock_id,
          source: 'airscort_dock',
          type: 'person_detected',
          score: e.score,
          latitude: e.latitude,
          longitude: e.longitude,
          occurred_at: e.occurred_at,
        }, ...prev]
      ));
    });
  }, [enabled, subscribePersonDetected]);

  // Answered by anyone, on any COP.
  useEffect(() => {
    if (!enabled) return;
    return subscribeTriggerResolved((e) => {
      setTriggers((prev) => prev.filter((t) => t.id !== e.id));
    });
  }, [enabled, subscribeTriggerResolved]);

  // The platform proposed an aircraft, or withdrew one it had proposed.
  //
  // MERGED, NEVER APPENDED. A proposal always lands on a detection the list already has - the
  // dispatcher decides about a row it has just stored - so a frame for an unknown id is one the
  // poll has not caught up with, and inventing a row from a proposal frame would draw a point
  // with no score and no age on it.
  useEffect(() => {
    if (!enabled) return;
    return subscribeTriggerProposal((e) => {
      setTriggers((prev) => prev.map((t) => (t.id === e.id ? {
        ...t,
        status: e.kind === 'proposed' ? 'proposed' : 'new',
        proposed_drone_id: e.kind === 'proposed' ? e.proposed_drone_id : null,
        proposed_altitude_m: e.kind === 'proposed' ? e.proposed_altitude_m : null,
        proposal_mode: e.kind === 'proposed' ? e.proposal_mode : null,
        proposal_expires_at: e.kind === 'proposed' ? e.proposal_expires_at : null,
        // CARRIED ON BOTH KINDS. On a proposal it is where the card finds the aircraft's callsign
        // - without it the operator is asked to approve "an aircraft". On a withdrawal it is the
        // account of an attempt that really happened, and the row keeps it: the server clears the
        // proposal columns and deliberately does not clear this one.
        proposal_considered: e.proposal_considered ?? t.proposal_considered ?? null,
        dispatch_refusal: e.dispatch_refusal ?? null,
      } : t)));
    });
  }, [enabled, subscribeTriggerProposal]);

  /**
   * Drop the point locally first, then tell the server.
   *
   * The optimistic order is deliberate: the operator has just acted, and a
   * point that lingers reads as "it did not work" and invites a second click -
   * which is a second sortie.
   */
  const resolve = useCallback(async (id: string, path: string, body: unknown) => {
    const removed = triggersRef.current.find((t) => t.id === id);
    setTriggers((prev) => prev.filter((t) => t.id !== id));
    try {
      await api.post(path, body);
    } catch (err) {
      // 404 and 409 are TERMINAL: the server is telling us this trigger is not
      // open any more - usually because a colleague answered it first. Putting
      // the point back would resurrect a detection that is already being flown
      // to, and every later click on it would 409 again until a full reload.
      // Roll back only for faults that might not have been applied at all.
      const status = err instanceof ApiRequestError ? err.status : undefined;
      const terminal = status === 404 || status === 409;
      if (removed && !terminal) {
        setTriggers((prev) => (prev.some((t) => t.id === id) ? prev : [removed, ...prev]));
      }
      throw err;
    }
  }, []);

  const markDispatched = useCallback(
    (id: string, missionId: string) => resolve(id, `/api/triggers/${id}/dispatched`, { mission_id: missionId }),
    [resolve],
  );

  const dismiss = useCallback(
    (id: string) => resolve(id, `/api/triggers/${id}/dismiss`, {}),
    [resolve],
  );

  /**
   * Approving KEEPS the point until the launch settles - unlike dispatching by hand, and the
   * difference is the card: `DroneMap` renders it from this list, so removing the row here
   * unmounts the card and the operator sees nothing for the whole bay sequence.
   *
   * IT DOES NOT SHARE `resolve`'s TERMINAL-409 RULE, and the difference matters. That rule was
   * written for "a colleague answered it first", where restoring the point would resurrect a
   * detection somebody is already flying to. The approve door answers 409 for a second reason:
   * the proposal LAPSED (its ten minutes are up), and in that case the detection is still open,
   * the person is still there, and nothing flew. Dropping the point would take a live detection
   * off the air picture until the next poll, up to 30 s later.
   *
   * So the row is not removed until the launch has actually succeeded. That answers the same
   * concern without a rollback: a failed approve leaves a live detection on the air picture
   * because it was never taken off it, and a point that was genuinely answered elsewhere
   * disappears on the next poll.
   */
  const approve = useCallback(async (id: string) => {
    // THE ROW IS NOT REMOVED UNTIL THE LAUNCH SETTLES (re-review, 2026-09-12).
    //
    // It used to go optimistically, on the click. `DroneMap` resolves the open card from this
    // very list, so removing the row UNMOUNTED the card in the same render - and the card's
    // "Opening bay - launching BRAVO" state, the whole point of holding it open, was never
    // painted. The operator got a vanished card, a vanished point, and up to fifty seconds of
    // nothing: exactly the silent launch the disabled state exists to replace. The component
    // test could not see it, because it renders the card with a static prop.
    //
    // The optimism was there so the button "visibly does something". The disabled, named,
    // in-flight row does that better, and it is honest: the point is still live until the
    // launch says otherwise. On failure there is now nothing to restore - the row never left -
    // which also removes the re-insert-at-the-front reordering the old path caused.
    const target = triggersRef.current.find((t) => t.id === id);
    // A BACKSTOP. This door is strictly LONGER than `/api/missions/:id/launch` - it adds the
    // lapse sweep, the approval CAS, the committed check and the mission build before the
    // launch, and the stamp after it - and it is not in the table `deploy/nginx/README.md`
    // sizes the 60 s proxy timeout against. With no bound at all a hung request leaves the card
    // disabled and the operator with no way back.
    //
    // IT CAN FIRE ON A SUCCESSFUL LAUNCH, and the caller's copy has to assume it did:
    // `launch-request.ts` already warns that a door which builds the mission before reaching
    // the bay sequence carries less margin than 55 s, and this one builds more than any of
    // them. The abort is INDETERMINATE - see `LAUNCH_TIMEOUT_MESSAGE`, which is what
    // `page.tsx` shows for it.
    const res = await api.post<{ data: { mission_id: string } }>(
      `/api/triggers/${id}/approve`, {}, { timeoutMs: LAUNCH_REQUEST_TIMEOUT_MS },
    );
    setTriggers((prev) => prev.filter((t) => t.id !== id));
    return { missionId: res.data.mission_id, droneId: target?.proposed_drone_id ?? null };
  }, []);

  /**
   * Rejecting does NOT remove the point, which is the whole difference from dismissing. It takes
   * the proposal off a detection that is still live, so the operator can answer it themselves.
   *
   * Applied optimistically for the same reason the removals are - the button must visibly do
   * something - and rolled back on any failure, because unlike a resolve there is no terminal
   * status here that would make restoring the proposal wrong.
   */
  const reject = useCallback(async (id: string) => {
    const before = triggersRef.current.find((t) => t.id === id);
    setTriggers((prev) => prev.map((t) => (t.id === id ? {
      ...t, status: 'new' as const, proposed_drone_id: null, proposed_altitude_m: null,
      proposal_mode: null, proposal_expires_at: null,
    } : t)));
    try {
      await api.post(`/api/triggers/${id}/reject`, {});
    } catch (err) {
      if (before) setTriggers((prev) => prev.map((t) => (t.id === id ? before : t)));
      throw err;
    }
  }, []);

  return { triggers, degraded, markDispatched, dismiss, approve, reject };
}
